import { IoCloseSharp } from "react-icons/io5";
import Button from "../../../../components/Button/button";
import {
  BlueTitle,
  ModalBody,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  TextError,
} from "../../../../shared/styles/global.styles";
import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { Label } from "../../../../components/Label/styles";
import {
  FilterSearchDadosFinanceirosForReportDto,
} from "../../../../shared/dtos/DadosFinanceiros.dto";
import SelectComponent from "../../../../components/Select/select";
import localPagamentoService from "../../../../services/LocalPagamentoService";
import { LocalPagamentoDto } from "../../../../shared/dtos/LocalPagamento.dto";
import lotacaoService from "../../../../services/LotacaoService";
import { DatePickerInputContainer } from "../../../../components/Input/styles";
import DatePicker from "react-datepicker";
import { ContainerCheckbox, LabelCheckbox } from "./styles";
import { Checkbox } from "@mui/material";
import { GetLotacaoDto } from "../../../../shared/dtos/Lotacao.dto";

interface Props {
  paramsSearch: FilterSearchDadosFinanceirosForReportDto;
  setParamsSearch: React.Dispatch<
    React.SetStateAction<FilterSearchDadosFinanceirosForReportDto>
  >;
  show: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const SearchModal: React.FC<Props> = ({
  paramsSearch,
  setParamsSearch,
  show,
  onClose,
  onSubmit,
}) => {
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [locaisPagamento, setLocaisPagamento] = useState<LocalPagamentoDto[]>(
    []
  );
  const [lotacoes, setLotacoes] = useState<GetLotacaoDto[]>([]);
  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([
    paramsSearch.dataInicioInlusao
      ? new Date(paramsSearch.dataInicioInlusao)
      : undefined,
    paramsSearch.dataFimInclusao
      ? new Date(paramsSearch.dataFimInclusao)
      : undefined,
  ]);
  const fetchLocaisPagamento = async () => {
    try {
      const response = await localPagamentoService.getAllLocaisPagamento();
      setLocaisPagamento(response);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro ao buscar locais de pagamento",
        allowOutsideClick: false,
      });
    }
  };
  const fetchLotacoes = async () => {
    try {
      const response = await lotacaoService.getAllLotacoes();
      setLotacoes(response);
    } catch (error) {
      Swal.fire({
        title: "Erro",
        text: "Erro ao buscar lotações. Por favor, tente novamente.",
        icon: "error",
        confirmButtonText: "Fechar",
        allowOutsideClick: false,
      });
    }
  };

  const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setDateRange([start ?? undefined, end ?? undefined]);

    if (start) {
      setParamsSearch({
        ...paramsSearch,
        dataInicioInlusao: start.toISOString().split("T")[0] + "T00:00:00",
      });
    }

    if (end) {
      setParamsSearch({
        ...paramsSearch,
        dataFimInclusao: end.toISOString().split("T")[0] + "T00:00:00",
      });
    }
  };

  const formatDateInput = (value: string): string => {
    // Remover caracteres não numéricos
    let numbers = value.replace(/\D/g, "");

    // Limitar a 8 dígitos (ddmmyyyy)
    numbers = numbers.substring(0, 8);

    // Aplicar máscara dd/mm/yyyy
    let formattedValue = "";
    if (numbers.length > 0) {
      formattedValue = numbers.substring(0, Math.min(2, numbers.length));

      if (numbers.length > 2) {
        formattedValue +=
          "/" + numbers.substring(2, Math.min(4, numbers.length));
      }

      if (numbers.length > 4) {
        formattedValue +=
          "/" + numbers.substring(4, Math.min(8, numbers.length));
      }
    }

    return formattedValue;
  };

  const handleDateInputChange = (e: any) => {
    if (!e || !e.target || typeof e.target.value !== "string") return;

    let value = e.target.value;

    // Não fazer nada se o campo estiver vazio
    if (!value) return;

    // Verificar se o valor contém hífen (separando intervalo de datas)
    const parts = value.split("-");

    if (parts.length > 1) {
      // Processar intervalo de datas (início - fim)
      let startDate = parts[0].trim();
      let endDate = parts[1].trim();

      // Formatar a primeira data
      startDate = formatDateInput(startDate);

      // Formatar a segunda data
      endDate = formatDateInput(endDate);

      // Recompor o valor com o formato correto
      e.target.value = `${startDate} - ${endDate}`;
    } else {
      // Processar uma única data
      const formattedDate = formatDateInput(value);

      // Verificar se a primeira data está completa (10 caracteres = dd/mm/aaaa)
      // Se estiver completa e não for uma operação de apagar (verificar se o valor atual é maior que o anterior)
      if (formattedDate.length === 10 && value.length >= formattedDate.length) {
        // Adicionar o hífen automaticamente
        e.target.value = `${formattedDate} - `;
      } else {
        e.target.value = formattedDate;
      }
    }
  };

  const validate = async () => {
    const newInvalidFields: string[] = [];
    if ((!dateRange[0] && !dateRange[1]) || !dateRange[0] || !dateRange[1]) {
      newInvalidFields.push("dataInclusao");
    }

    // Verificar se a data final é menor que a data inicial (período inválido)
    if (dateRange[0] && dateRange[1] && dateRange[1] < dateRange[0]) {
      newInvalidFields.push("dataInclusao");

      Swal.fire({
        icon: "error",
        title: "Período inválido",
        text: "A data final não pode ser menor que a data inicial.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });

      setInvalidFields(newInvalidFields);
      return;
    }

    // Verificar se o período é muito longo
    if (dateRange[0] && dateRange[1]) {
      const diffTime = Math.abs(
        dateRange[1].getTime() - dateRange[0].getTime()
      );
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Se período for maior que 31 dias (aproximadamente 1 mês), exibir alerta
      if (diffDays > 31) {
        const result = await Swal.fire({
          title: "Atenção",
          text: "O período selecionado é longo e pode causar lentidão. Deseja continuar mesmo assim?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim, continuar",
          cancelButtonText: "Não, ajustar período",
          allowOutsideClick: false,
        });

        if (!result.isConfirmed) {
          return;
        }
      }
    }

    if (newInvalidFields.length > 0) {
      setInvalidFields(newInvalidFields);
      Swal.fire({
        icon: "error",
        title: "Erro nos campos",
        text: "Por favor, preencha todos os campos obrigatórios.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });
      return;
    }

    setInvalidFields([]);
    onSubmit();
    onClose();
  };

  const clearError = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name } = event.target;

    setInvalidFields((prev) => prev.filter((field) => field !== name));
  };

  useEffect(() => {
    fetchLocaisPagamento();
    fetchLotacoes();
  }, []);

  return (
    <ModalContainer show={show}>
      <ModalContent style={{ marginTop: "2%" }} width={40}>
        <Button
          type="button"
          schemaButton="x-close"
          action={() => onClose()}
          icon={<IoCloseSharp size={30} />}
          position="absolute"
          top={10}
          right={10}
        />
        <ModalHeader>
          <BlueTitle>Gerar relatório</BlueTitle>
        </ModalHeader>
        <ModalBody>
          <div>
            <Label htmlFor="lotacao">Lotação</Label>
            <SelectComponent
              color="black"
              placeholder="Todas Lotações"
              isPlaceHolder={false}
              id="lotacao"
              width={Number("100%")}
              name="lotacao"
              value={paramsSearch.idLotacao?.toString() ?? ""}
              onChange={(e) => {
                if (e.target.value === "0") {
                  setParamsSearch({
                    ...paramsSearch,
                    idLotacao: 0,
                    lotacao: { id: 0, descricao: "Sem lotação" },
                  });
                } else {
                  const lotacao = lotacoes.find(
                    (lotacao) => lotacao.id === parseInt(e.target.value)
                  );
                  setParamsSearch({
                    ...paramsSearch,
                    idLotacao: lotacao?.id,
                    lotacao: lotacao,
                  });
                }
                clearError(e);
              }}
              options={[
                { value: "0", label: "Sem lotação" },
                ...lotacoes.map((lotacao) => ({
                  value: lotacao.id!.toString(),
                  label: lotacao.descricao,
                })),
              ]}
              errorMessage={
                invalidFields.includes("lotacao")
                  ? "A seleção da lotação é obrigatória"
                  : ""
              }
            />
          </div>
          <div>
            <Label htmlFor="localPagamento">Local de Pagamento</Label>
            <SelectComponent
              color="black"
              placeholder="Todos locais de pagamento"
              isPlaceHolder={false}
              id="localPagamento"
              width={Number("100%")}
              name="localPagamento"
              value={paramsSearch.idLocalPagamento?.toString() ?? ""}
              onChange={(e) => {
                const localPagamento = locaisPagamento.find(
                  (localPagamento) =>
                    localPagamento.id === parseInt(e.target.value)
                );
                setParamsSearch({
                  ...paramsSearch,
                  idLocalPagamento: Number(e.target.value),
                  localPagamento: localPagamento,
                });
                clearError(e);
              }}
              options={locaisPagamento.map((local) => ({
                value: local.id!.toString(),
                label: local.descricao,
              }))}
              errorMessage={
                invalidFields.includes("localPagamento")
                  ? "A seleção do local de pagamento é obrigatória"
                  : ""
              }
            />
          </div>
          <div>
            <Label className="filtro-label" htmlFor="dataInclusao">
              Data de inclusão
            </Label>
            <DatePickerInputContainer
              width={Number("100%")}
              hasIconLeft={false}
              hasIconRight={false}
              error={invalidFields.includes("dataInclusao") ? true : false}
              className={dateRange[0] || dateRange[1] ? "has-date-content" : ""}
            >
              <DatePicker
                selectsRange={true}
                startDate={dateRange[0] as Date | undefined}
                endDate={dateRange[1] as Date | undefined}
                onChange={(dates) =>
                  handleDateRangeChange(dates as [Date | null, Date | null])
                }
                onChangeRaw={handleDateInputChange}
                calendarStartDay={0}
                className={`date-picker-input placeholder-custom ${
                  dateRange[0] || dateRange[1] ? "has-value" : "no-value"
                }`}
                dateFormat="dd/MM/yyyy"
                placeholderText="00/00/0000 - 00/00/0000"
                showPopperArrow={false}
                popperPlacement="bottom-start"
                isClearable={false}
                showYearDropdown
                showMonthDropdown
                scrollableMonthYearDropdown
                wrapperClassName={
                  dateRange[0] || dateRange[1]
                    ? "date-wrapper-filled"
                    : "date-wrapper-empty"
                }
                locale="pt-BR"
                autoComplete="off"
                allowSameDay
                onKeyDown={(e) => {
                  // Permite números, barras, backspace e teclas de navegação
                  if (
                    !/[0-9\/]/.test(e.key) &&
                    e.key !== "Backspace" &&
                    e.key !== "Delete" &&
                    e.key !== "ArrowLeft" &&
                    e.key !== "ArrowRight" &&
                    e.key !== "Tab"
                  ) {
                    e.preventDefault();
                  }

                  // Adiciona barras automaticamente
                  const input = e.currentTarget as HTMLInputElement;
                  if (/[0-9]/.test(e.key)) {
                    const value = input.value;
                    if (value.length === 2 || value.length === 5) {
                      input.value = value + "/";
                    }
                  }
                }}
              />
            </DatePickerInputContainer>
            {invalidFields.includes("dataInclusao") && (
              <TextError>
                A seleção do período da data de inclusão é obrigatória
              </TextError>
            )}
          </div>
          <ContainerCheckbox>
            <Checkbox
              checked={paramsSearch.lowerValues}
              onChange={(e) => {
                setParamsSearch({
                  ...paramsSearch,
                  lowerValues: !paramsSearch.lowerValues,
                });
              }}
            />
            <LabelCheckbox htmlFor={`reajuste-monetario`}>
              Somente valores menores
            </LabelCheckbox>
          </ContainerCheckbox>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            action={() => {
              validate();
            }}
            schemaButton="full-primary"
          >
            Gerar relatório
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalContainer>
  );
};

export default SearchModal;
